import { useMemo } from "react";
import { useUser, useChatSelection, useChatSessionRetry } from "@platform/store";
import {
  useChat,
  useChatActions,
  useChatProgramId,
  useTalkJSData,
} from "@platform/hooks/useChat";
import { useChatVisibility } from "@platform/hooks/useChatVisibility";
import { useFetchUserCircles } from "@core/features/circle";
import {
  useResolvedChatCircle,
  useChatUnreadConversationIds,
} from "@core/features/chat";
import { useTracking } from "@core/features/tracking";
import { parseBrowserAttribution } from "@core/features/videoMeeting/utils/getTrackingMetadata";
import { ChatSheet } from "./ChatSheet";

export function ChatSheetContainer() {
  const { isOpen } = useChat();
  const { closeChat } = useChatActions();
  const talkjsData = useTalkJSData();
  const user = useUser();
  const programId = useChatProgramId();
  const { data: circlesData } = useFetchUserCircles({ user });
  const userCircles = circlesData?.circles ?? null;
  const { trackEvent } = useTracking();

  const { shouldShowChat, isLoading: isChatStatusLoading } = useChatVisibility({
    userCircles,
  });

  const { selectedCircleByProgram, setSelectedCircle, clearSelectedCircle } =
    useChatSelection();
  const { key: sessionRetryKey, bump: bumpSessionRetry } =
    useChatSessionRetry();

  // Resolve which circle's conversation should render in the dashboard
  // surface. The hook handles auto-select for single-circle users,
  // honors a persisted selection when valid, and falls back to the
  // picker UI when needed. Stale selections are evicted as a side
  // effect so the store doesn't accumulate dead entries.
  const resolved = useResolvedChatCircle({
    userCircles,
    programId,
    talkjsData,
    selectedCircleByProgram,
    clearSelectedCircle,
  });

  // Per-conversation unread state for the switcher's dots. Scoped to
  // the active program's conversations so unread state from other
  // programs (which the dashboard isn't currently surfacing) doesn't
  // light up dots in this sheet.
  const allowedConversationIds = useMemo(
    () =>
      talkjsData?.conversations.map((c) => c.talkjs_conversation_id) ?? [],
    [talkjsData]
  );
  const unreadConversationIds = useChatUnreadConversationIds(
    allowedConversationIds
  );

  // Don't render chat sheet if chat is disabled or missing required data
  if (!isChatStatusLoading && !shouldShowChat) {
    return null;
  }

  // Don't render if required data is missing
  if (!talkjsData || !user) {
    return null;
  }

  const handleSelectCircle = (circleId: number) => {
    if (programId != null) {
      setSelectedCircle(programId, circleId);
    }
  };

  // REQUIRED per docs/chat-stuck-spinner-fix-plan.md §3.7. Both events
  // MUST include browser attribution so fleet-wide cohort analysis can
  // answer Chrome-148-vs-cross-browser questions. Reuse the platform
  // helper from videoMeeting/utils — do NOT re-parse navigator.userAgent
  // in the chat domain.
  const handleSessionRetry = (ctx: { conversationId: string | null }) => {
    const { browser_name, browser_version } = parseBrowserAttribution();
    trackEvent({
      name: "chat.chatbox.retry_clicked",
      params: {
        surface: "sheet",
        conversation_id: ctx.conversationId,
        browser_name,
        browser_version,
      },
    });
    bumpSessionRetry();
  };

  const handleChatboxStuckTracked = (ctx: {
    conversationId: string | null;
  }) => {
    const { browser_name, browser_version } = parseBrowserAttribution();
    trackEvent({
      name: "chat.chatbox.stuck_detector.tripped",
      params: {
        surface: "sheet",
        route:
          typeof window !== "undefined" ? window.location.pathname : "<ssr>",
        conversation_id: ctx.conversationId,
        browser_name,
        browser_version,
      },
    });
  };

  return (
    <ChatSheet
      isLoading={isChatStatusLoading}
      isOpen={isOpen}
      onClose={closeChat}
      talkjsData={talkjsData}
      user={user}
      resolved={resolved}
      unreadConversationIds={unreadConversationIds}
      onSelectCircle={handleSelectCircle}
      sessionRetryKey={sessionRetryKey}
      onSessionRetry={handleSessionRetry}
      onChatboxStuckTracked={handleChatboxStuckTracked}
    />
  );
}

export default ChatSheetContainer;
